<template>
  <div class="coupon-landing-page">
    <div data-test-id="coupon-hero" class="py-12 l:pb-16" :class="layoutClasses">
      <div class="container flex flex-col items-center">
        <div class="flex l:flex-col l:flex-col-reverse gap-4 items-center mb-4">
          <span v-if="useBlackFridayLayout" class="uppercase text-h4 l:text-h35 font-bold leading-8">
            {{ $t("pages.coupon.blackFridaySale") }}
          </span>
          <div
            data-test-id="limited-time-offer"
            class="flex items-center bg-raspberry rounded text-white text-sm font-medium px-2 py-1"
          >
            <timer-icon class="w-5 h-5 flex-shrink-0 mr-1" />
            <div class="w-full text-center">
              {{ $t("pages.coupon.limitedOfferBadge") }}
            </div>
          </div>
        </div>
        <h1
          data-test-id="coupon-headline"
          class="text-center text-h1 l:text-h0 font-bold mb-4"
          :class="{
            'text-white': useBlackFridayLayout,
            'text-raspberry': !useBlackFridayLayout
          }"
          v-html="headlineWithFallback"
        />

        <h2
          data-test-id="coupon-subheadline"
          class="text-center text-h5 mb-27 l:mb-8 font-normal"
          :class="{
            'text-white': useBlackFridayLayout,
            'text-midnight': !useBlackFridayLayout
          }"
          v-html="subheadlineWithFallback"
        />

        <img :src="couponBookImageUrl" class="hidden l:block mb-16 min-h-34.5 w-full max-w-116 mx-auto px-2" />

        <offer-box
          :offer-name="offerNameWithFallback"
          :subscription-period="subscriptionPeriod"
          :trial-days="trialDays"
          :show-border="!useBlackFridayLayout"
          :discount-value="discountValue"
        >
          <template slot="originalPrice">
            <span v-html="originalPrice" />
          </template>
          <template slot="discountedPrice">
            <span v-html="discountedPrice" />
          </template>
          <template slot="cta">
            <coupon-cta
              class="flex mb-8 w-full"
              :cta-copy="ctaCopyWithFallback"
              :cta-link="ctaLinkWithFallback"
              :open-signup="openSignup"
              :open-login="openLogin"
              :cta-button-data="ctaButtonData"
              :login-path="plan.loginPath"
              :signup-path="plan.signupPath"
            />
          </template>
        </offer-box>
        <div />
      </div>
    </div>
    <app-reviews :cta-copy="ctaCopyWithFallback">
      <template slot="cta">
        <coupon-cta
          class="flex mb-8 w-full max-w-74"
          :cta-copy="ctaCopyWithFallback"
          :cta-link="ctaLinkWithFallback"
          :open-signup="openSignup"
          :open-login="openLogin"
          :cta-button-data="ctaButtonData"
          :login-path="plan.loginPath"
          :signup-path="plan.signupPath"
        />
      </template>
    </app-reviews>

    <p class="text-caption text-center max-w-2xl mx-auto mb-12 l:mb-16 px-4">
      {{ disclaimerCopy }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import AppReviews from "@/features/app-reviews"
import OfferBox from "@/component-library/offer-box"
import TimerIcon from "@/component-library/icons/timer-icon"

import CouponCta from "./coupon-cta"
const IMAGE_BASE_URL = "https://static.blinkist.com"
/*
  tailwind classes added to be used on dynamic components:
  text-green text-midnight text-blue text-orchid-violed text-raspberry text-summer-yello font-blinkist-serif
*/

/*
  Considerations when migrating this out of the web
  We have in the cta buttons classes that triggers some special ready stuff from legacy web:
  - js-open-signup-popup: Opens a popup instead of redirecting the user.
*/
export default {
  name: "CouponLandingPage",
  components: {
    AppReviews,
    CouponCta,
    OfferBox,
    TimerIcon
  },
  props: {
    plan: {
      type: Object,
      required: true
    },
    coupon: {
      type: Object,
      required: true
    },
    landingPage: {
      type: Object
    },
    discountPercentage: {
      type: Number
    }
  },
  computed: {
    ...mapGetters(["isUserLoggedIn", "hasTrialed"]),
    headline() {
      return this.landingPage[`header_${this.$i18n.locale}`]
    },
    subheadline() {
      return this.landingPage[`subheader_${this.$i18n.locale}`]
    },
    offerName() {
      return this.landingPage[`offer_name_${this.$i18n.locale}`]
    },
    ctaCopy() {
      return this.landingPage[`cta_copy_${this.$i18n.locale}`]
    },
    ctaLink() {
      return this.landingPage["cta_link"]
    },
    price() {
      return this.plan.price
    },
    originalPrice() {
      return this.price.original.markup
    },
    discountedPrice() {
      return this.price.final.markup
    },
    discountValue() {
      return this.price.discount.markup
    },
    trialDays() {
      if (this.isUserLoggedIn && this.hasTrialed) {
        return 0
      }

      return this.plan.trialDays
    },
    useBlackFridayLayout() {
      if (!window) {
        return false
      }
      const url = new URL(window.location.href)
      return url.searchParams.get("bf") === "true"
    },
    offerNameWithFallback() {
      return this.offerName || this.$t("pages.coupon.defaultOfferName")
    },
    layoutClasses() {
      if (this.useBlackFridayLayout) {
        return "bg-black-friday text-white"
      }

      return "bg-background-red"
    },
    timer() {
      const now = new Date()
      return now.setMinutes(now.getMinutes() + 20)
    },
    headlineWithFallback() {
      const headlineText = this.headline || this.$t("pages.coupon.defaultHeadline")
      return this.replaceDiscountAttributes(headlineText)
    },
    subheadlineWithFallback() {
      const subheadlineText = this.subheadline || this.$t("pages.coupon.defaultSubheadline")
      return this.replaceDiscountAttributes(subheadlineText)
    },
    couponBookImageUrl() {
      return `${IMAGE_BASE_URL}/coupon-pages/coupon-books-${this.$i18n.locale}.png`
    },
    timerWidth() {
      return this.$i18n.locale === "en" ? "w-28" : "w-33.5"
    },
    showSignup() {
      if (!window) {
        return false
      }
      const url = new URL(window.location.href)
      return url.searchParams.get("show_login") === "0"
    },
    openLogin() {
      return !this.ctaLink && !this.showSignup
    },
    openSignup() {
      return !this.ctaLink && this.showSignup
    },
    disclaimerCopy() {
      return this.coupon.isSingleUse
        ? this.$t("pages.coupon.disclaimerTextOneTime")
        : this.$t("pages.coupon.disclaimerTextLifetime")
    },
    ctaCopyWithFallback() {
      return this.ctaCopy || this.$t("pages.coupon.defaultCtaCopy")
    },
    ctaLinkWithFallback() {
      if (this.isUserLoggedIn) {
        return this.plan.link
      }
      if (this.ctaLink) {
        return this.ctaLink
      }

      if (this.showSignup) {
        return this.plan.signupPath
      }

      return this.plan.loginPath
    },
    ctaButtonData() {
      /* eslint-disable camelcase */
      return {
        sku: this.plan.sku,
        signup_path: this.plan.signup_path,
        event_category: "body-20180308-pricing_plan",
        event_action: "click_signup",
        event_depth: "1",
        event_name: this.plan.duration_in_english
      }
      /* eslint-enable camelcase */
    },
    subscriptionPeriod() {
      return this.$t(`pages.coupon.subscriptionPeriod.${this.plan.type}`)
    }
  },
  methods: {
    replaceDiscountAttributes(text) {
      return text
        .replace(/{discountPercentage}/g, this.discountPercentage)
        .replace(/{discountValue}/g, this.discountValue)
    }
  }
}
</script>

<style>
.coupon-landing-page p {
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  margin: 0;
  padding: 0;
}
</style>
